import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card, CardBody, Row, Col, Form, Image, Container, Breadcrumb, Alert
} from "react-bootstrap";
import { MEDIA_BASE_URL } from "@/api/api";
import { useDispatch, useSelector } from "react-redux";
import { FaDownload, FaThumbsUp, FaTimes } from "react-icons/fa";
import { MdOutlineAttachFile, MdPendingActions } from "react-icons/md";
import { FaImage } from "react-icons/fa6";
import {
  taskDetail, addTaskStep, completeTaskStep, deleteTaskStep,
  startTask, submitTask
} from "@/store/tasksApi/tasksThunk";
import formatDate from "../utils/formatDate";
import toast from "react-hot-toast";
import TaskActionButton from "@/components/TaskButton/TaskButton";
import { Timeline, Statistic } from "antd";
import truncateChars from "../utils/cutWords";
import { HomeOutlined } from "@ant-design/icons";
import { getFileName, normalizeUrl } from "../utils/normalizeUrl";
import { Button } from 'antd';
import {CheckOutlined} from "@ant-design/icons"
const { Countdown } = Statistic;

const STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
  PENDING_AFTER_REJECTION: "pending_after_rejection",
};

const STATUS_LABEL = {
  pending: "Yaratildi",
  in_progress: "Bajarilmoqda",
  submitted: "Topshirildi",
  approved: "Tasdiqlandi",
  rejected: "Rad etildi",
  pending_after_rejection: "Rad etilgan",
};

const STATUS_COLOR = {
  pending: "gray",
  in_progress: "blue",
  submitted: "#faad14",
  approved: "green",
  rejected: "red",
  pending_after_rejection: "red",
};

// 25-09-25 14:35 format
const formatShortDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${dd}-${mm}-${yy} ${hh}:${mi}`;
};

const statusAlertMap = {
  [STATUS.SUBMITTED]: { variant: "warning", text: "Topshiriq ko‘rib chiqilmoqda.", icon: <MdPendingActions className="ms-2" /> },
  [STATUS.APPROVED]: { variant: "success", text: "Topshiriq tasdiqlangan.", icon: <FaThumbsUp className="ms-2" /> },
  [STATUS.REJECTED]: { variant: "danger", text: "Topshiriq rad etilgan.", icon: <FaTimes className="ms-2" /> },
};

const DAY = 24 * 60 * 60 * 1000;

const TasksDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { tasksDetailData: task } = useSelector((state) => state.tasksApi);

  const [newStep, setNewStep] = useState("");
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (id) dispatch(taskDetail(id));
  }, [dispatch, id]);

  const isPending = task?.status === STATUS.PENDING;
  const isInProgress = task?.status === STATUS.IN_PROGRESS;
  const isTerminal = [STATUS.SUBMITTED, STATUS.APPROVED, STATUS.REJECTED].includes(task?.status);

  // === TIMER (deadline gacha) ===
  const deadlineTs = useMemo(() => (task?.deadline ? new Date(task.deadline).getTime() : null), [task?.deadline]);
  const remainingMs = useMemo(() => (deadlineTs ? deadlineTs - Date.now() : null), [deadlineTs]);

  const timerVariant = useMemo(() => {
    if (remainingMs == null) return "success";
    if (remainingMs <= 0) return "danger";
    if (remainingMs <= 1 * DAY) return "danger";
    if (remainingMs <= 3 * DAY) return "warning";
    return "success";
  }, [remainingMs]);

  const timerBoxClass =
    timerVariant === "danger"
      ? "bg-danger-subtle border border-danger text-danger"
      : timerVariant === "warning"
        ? "bg-warning-subtle border border-warning text-warning"
        : "bg-success-subtle border border-success text-success";

  // === Steps actions ===
  const toggleStep = (stepId) => {
    if (isTerminal) return;

    dispatch(completeTaskStep({ taskId: (task.id), stepId: (stepId) }))
      .then((response) => {
        if (response?.meta?.requestStatus === "fulfilled") {
          if (id) dispatch(taskDetail(id));
          toast.success("Step holati yangilandi");
        } else {
          toast.error(response?.payload || "Bu qadam allaqachon bajarilgan");
        }
      });
  };

  const deleteStep = (stepId) => {
    if (isTerminal) return;
    dispatch(deleteTaskStep({ taskId: (task.id), stepId: (stepId) }))
      .then((response) => {
        if (response?.meta?.requestStatus === "fulfilled") {
          if (id) dispatch(taskDetail(id));
          toast.success("Step o‘chirildi");
        } else {
          toast.error(response?.payload || "Bajarilgan qadamni o‘chirib bo‘lmaydi");
        }
      });
  };

  const handleAction = () => {
    if (isPending) {
      dispatch(startTask(task.id)).then((response) => {
        if (response?.meta?.requestStatus === "fulfilled") {
          if (id) dispatch(taskDetail(id));
          toast.success("Topshiriq qabul qilindi");
        } else {
          toast.error("Topshiriqni qabul qilishda xatolik yuz berdi");
        }
      });
    } else if (isInProgress) {
      const formData = new FormData();
      if (file) formData.append("file", file);
      if (image) formData.append("image", image);

      dispatch(submitTask({ taskId: task.id, formData })).then((response) => {
        if (response?.meta?.requestStatus === "fulfilled") {
          if (id) dispatch(taskDetail(id));
          toast.success("Topshiriq yuborildi");
          setFile(null);
          setImage(null);
        } else {
          toast.error(response?.payload || "Topshiriqni topshirishda xatolik yuz berdi");
        }
      });
    }
  };

  const handleAddStep = () => {
    if (isTerminal || !newStep.trim()) return;
    const stepData = { title: newStep, taskId: task.id };
    dispatch(addTaskStep(stepData)).then((response) => {
      if (response?.meta?.requestStatus === "fulfilled") {
        toast.success("Yangi step qo‘shildi");
        if (id) dispatch(taskDetail(id));
      } else {
        toast.error(response?.payload || "Step qo‘shishda xatolik yuz berdi");
      }
    });
    setNewStep("");
  };

  // Timeline data
  const rawHistory = Array.isArray(task?.status_history) ? task.status_history : [];
  const lastStatus = rawHistory.length ? rawHistory[rawHistory.length - 1].status : null;
  const timelineHistory = rawHistory.slice(1); // birinchi (pending) ko‘rsatilmaydi
  const pendingText = lastStatus === "approved" ? undefined : "Kutilmoqda";
  const timelineItems = timelineHistory.map((h) => {
    const title = `${STATUS_LABEL[h.status] || h.status}${h.reason ? " — " + h.reason : ""}`;
    return {
      color: STATUS_COLOR[h.status] || "gray",
      children: (
        <div>
          <div className="d-flex flex-column justify-content-start">
            <strong>{title}</strong>
            <small className="text-muted">{formatShortDateTime(h.timestamp)}</small>
          </div>
        </div>
      ),
    };
  });

  // HERO image (terminalda attachments-dagi birinchi rasm, aks holda task.image)
  const firstAttachmentImage = isTerminal
    ? (task?.attachments || []).find((a) => a.image_url)?.image_url
    : null;

  const formatUrl = (url) => {
    if (!url) return null;
    return url.replace("/apis", ""); // faqat birinchi uchragan /apis ni olib tashlaydi
  };

  const heroImageSrc = firstAttachmentImage
    ? (firstAttachmentImage.startsWith("http")
      ? formatUrl(firstAttachmentImage)
      : MEDIA_BASE_URL + firstAttachmentImage
    )
    : (task?.image ? formatUrl(MEDIA_BASE_URL + task.image) : null);

  return (
    <Container fluid>
      <Breadcrumb className="py-2">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}> <HomeOutlined /></Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/tasks/list" }}>Topshiriqlar</Breadcrumb.Item>
        <Breadcrumb.Item active>{truncateChars(task?.title || "", 50)}</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="justify-content-center">
        <Col>
          <Card className="border-0 rounded-3 shadow-sm">
            <CardBody>

              {deadlineTs && (isPending || isInProgress) && (
                <div
                  className={`p-3 rounded-3 d-flex flex-wrap align-items-center justify-content-between mb-3 ${timerBoxClass}`}
                >
                  <div className="me-3">
                    <div className="fw-semibold">Deadline gacha</div>
                    <small className="opacity-75">
                      {formatShortDateTime(task?.deadline)} gacha
                    </small>
                  </div>
                  {remainingMs > 0 ? (
                    <Countdown
                      value={deadlineTs}
                      format="DD [kun] HH:mm:ss"
                      valueStyle={{ fontWeight: 700, fontSize: 22 }}
                    />
                  ) : (
                    <div className="fw-bold" style={{ fontSize: 15 }}>
                      Muddati tugagan
                    </div>
                  )}
                </div>
              )}

              {/* Rejection Alert */}
              {task?.was_rejected && (
                <Alert variant="danger" className="mb-3">
                  <div className="d-flex align-items-start">
                    <FaTimes className="me-2 mt-1" size={18} />
                    <div className="flex-grow-1">
                      <div className="fw-bold mb-1">Bu topshiriq rad etilgan</div>
                      {task?.last_rejection_at && (
                        <div className="small text-muted mb-2">
                          {formatShortDateTime(task.last_rejection_at)}
                        </div>
                      )}
                      {task?.last_rejection_reason && (
                        <div className="small bg-white bg-opacity-75 p-2 rounded">
                          <strong>Sabab:</strong> {task.last_rejection_reason}
                        </div>
                      )}
                    </div>
                  </div>
                </Alert>
              )}

              <div className="d-flex align-items-center mb-4 p-3 rounded border-primary">
                <Image
                  src={task?.approver?.picture || "https://via.placeholder.com/60"}
                  roundedCircle width={50} height={50}
                  className="me-3 shadow-sm object-fit-cover"
                />
                <div>
                  <h6 className="mb-0 fw-bold">
                    {task?.approver?.first_name} {task?.approver?.surname}{" "}
                  </h6>
                  <small className="text-muted">Ijroni ta’minlash uchun mas’ul</small>
                </div>
              </div>

              <h4 className="mb-3 text-primary">{task?.title}</h4>

              <Row className="g-4 align-items-start mb-4">
                {/* Left: description */}
                <Col md={6}>
                  <div className="p-3 border rounded">
                    <h6 className="text-muted mb-2">Tavsif</h6>
                    <p className="mb-3">{task?.description || "—"}</p>
                  </div>
                </Col>

                {/* Right: image + meta */}
                <Col md={6}>
                  <div className="ratio ratio-16x9 border rounded overflow-hidden">
                    {heroImageSrc ? (
                      <Image
                        src={heroImageSrc}
                        alt="Task image"
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-body-tertiary text-muted">
                        <FaImage className="me-2" /> Rasm mavjud emas
                      </div>
                    )}
                  </div>

                  {/* Root file (terminal bo'lmaganda) */}
                  {!isTerminal && task?.file && (
                    <div className="mt-3">
                      <a
                        className="btn btn-outline-secondary"
                        href={MEDIA_BASE_URL + task.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textTransform: "none" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <FaDownload />
                          <span>Faylni ochish</span>
                        </div>
                      </a>
                    </div>
                  )}

                  <div className="small mt-3">
                    <div className="mb-1"><strong>Yaratilgan:</strong> {formatDate(task?.created_at)}</div>
                    <div><strong>Muddati:</strong> {formatDate(task?.deadline)}</div>
                  </div>
                </Col>
              </Row>
              {/* ==== /HERO SECTION ==== */}

              {/* Terminal holatlarda attachments */}
              <div className="row">

                {/* Timeline */}
                {timelineItems.length > 0 && (
                  <div className="mb-4 col-lg-4">
                    <h5 className="mt-4 mb-3">Holatlar tarixi</h5>
                    <Timeline pending={pendingText} items={timelineItems} />
                  </div>
                )}

                {isTerminal && Boolean(task?.attachments?.length) && (
                  <div className="mb-4 col-lg-8">
                    <h5 className="mb-2">Biriktirilgan fayllar</h5>
                    <Row className="g-3">
                      {task.attachments.map((att) => (
                        <Col md={6} key={att.id}>
                          {att.image_url && (
                            <div className="mb-2">
                              <a
                                className="btn btn-outline-secondary"
                                href={MEDIA_BASE_URL + att.image_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textTransform: "none" }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <FaDownload />
                                  <span>{getFileName(att.image_url)}</span>
                                </div>
                              </a>
                            </div>
                          )}
                          {att.file_url && (
                            <a
                              className="btn btn-outline-secondary"
                              href={MEDIA_BASE_URL + att.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textTransform: "none" }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <FaDownload />
                                <span>{getFileName(att.file_url)}</span>
                              </div>
                            </a>
                          )}
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </div>

              {/* Steps */}
              <h5 className="mt-4">Vazifalar</h5>
              <ul className="list-group mb-3">
                {task?.steps?.map((step) => (
                  <li
                    key={step.id}
                    className={`list-group-item d-flex justify-content-between align-items-center ${step.is_completed ? "list-group-item-success" : ""}`}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        checked={step.is_completed}
                        disabled={isTerminal}
                        onChange={() => toggleStep(step.id)}
                      />
                      <span style={{ textDecoration: step.is_completed ? "none" : "none" }}>
                        {step.title}
                      </span>
                    </div>

                    {!isTerminal && (
                      <Button variant="outline-danger" size="sm" onClick={() => deleteStep(step.id)}>
                        O‘chirish
                      </Button>
                    )}
                  </li>
                ))}
                {(!task?.steps || task.steps.length === 0) && (
                  <li className="list-group-item text-muted">Hozircha step qo‘shilmagan</li>
                )}
              </ul>

              {/* Step qo‘shish */}
              {!isTerminal && (
                <div className="d-flex mb-4 align-items-center">
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Step qo‘shish..."
                    value={newStep}
                    onChange={(e) => setNewStep(e.target.value)}
                  />
                  <Button type="primary" icon={<CheckOutlined />} className="ms-2" onClick={handleAddStep}>
                    Qo‘shish
                  </Button>
                </div>
              )}

              {/* Fayl/rasm yuklash — faqat IN_PROGRESS */}
              {isInProgress && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-2">Fayl yoki rasm yuklang:</h6>
                  <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>
                      <div className="d-flex align-items-center gap-1">
                        <MdOutlineAttachFile /> Fayl yuklash
                      </div>
                    </Form.Label>
                    <Form.Control  size="sm" type="file" onChange={(e) => setFile(e.target.files[0])} />
                  </Form.Group>

                  <Form.Group controlId="formImage" className="mb-3">
                    <Form.Label>
                      <div className="d-flex align-items-center gap-1">
                        <FaImage /> Rasm yuklash
                      </div>
                    </Form.Label>
                    <Form.Control  size="sm" type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
                  </Form.Group>
                </div>
              )}

              {/* Action button — pending yoki in_progress */}
              {(isPending || isInProgress) && (
                <div className="text-end">
                  <TaskActionButton status={task.status} handleAction={handleAction} />
                </div>
              )}
              {/* Terminal status alert */}
              {isTerminal && (
                <Alert
                  variant={statusAlertMap[task.status].variant}
                  className="d-flex align-items-center gap-2 justify-content-center"
                >
                  {statusAlertMap[task.status].icon} {statusAlertMap[task.status].text}
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TasksDetail;
